/* eslint-disable react/no-unknown-property */
import { FC, PropsWithChildren, useMemo, useRef, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import PdImage from '../pd-image/pd-image'

export interface PdMediaProps {
  // @deprecated
  type?: 'image' | 'video'
  src: string
  h5Src?: string
  mask?: boolean
  alt?: string
  useLoader?: boolean
  stopAutoplay?: boolean
  muted?: boolean
  videoCommand?: 'PLAY' | 'PAUSE'
  linkHref?: string
  priority?: boolean
  centerChildren?: boolean
  object?: 'cover' | 'contain' | 'fill'
  enableHoverScale?: boolean
  imageClassName?: string
  showShadow?: boolean
  className?: string
  loop?: boolean
  /** 由素材固有比例撑开高度，默认保持铺满父容器。 */
  autoHeight?: boolean
}

// 检测是否为微信内置浏览器
const isWeChatBrowser = (): boolean => {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent.toLowerCase()
  return /micromessenger/i.test(ua)
}

const PdMedia: FC<PropsWithChildren<PdMediaProps>> = ({
  children,
  src = '',
  h5Src,
  alt,
  mask = true,
  useLoader,
  stopAutoplay = false,
  muted,
  videoCommand,
  linkHref,
  priority,
  centerChildren,
  object = 'cover',
  enableHoverScale = false,
  imageClassName,
  showShadow = true,
  className,
  loop = true,
  autoHeight = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasUserInteractedRef = useRef(false)

  const type = useMemo(() => {
    const lowerCaseSrc = src.toLowerCase()
    if (
      lowerCaseSrc.endsWith('.mp4') ||
      lowerCaseSrc.endsWith('.webm') ||
      lowerCaseSrc.endsWith('.avi') ||
      lowerCaseSrc.endsWith('.mov')
    ) {
      return 'video'
    }
    return 'image'
  }, [src])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.src = src
    }
  }, [src])

  // 播放视频函数
  const playVideo = useCallback(async () => {
    if (!videoRef.current) return
    try {
      videoRef.current.muted = muted ?? true
      await videoRef.current.play()
    } catch {
      // 播放失败，忽略
    }
  }, [muted])

  // 用户交互处理（统一处理点击和触摸）
  const handleUserInteraction = useCallback(
    async (e?: React.MouseEvent<HTMLVideoElement> | React.TouchEvent<HTMLVideoElement>) => {
      if (!videoRef.current) return

      hasUserInteractedRef.current = true
      videoRef.current.muted = muted ?? true

      // 如果有链接，不阻止事件冒泡，让链接可以正常工作
      if (linkHref) {
        // 只尝试播放，不阻止链接跳转
        videoRef.current.play().catch(() => {})
        return
      }

      // 没有链接时，可以阻止默认行为（防止视频的默认播放行为）
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }

      // 立即尝试播放
      try {
        await videoRef.current.play()
      } catch {
        // 如果失败，等待视频加载完成后再播放
        const tryPlayWhenReady = () => {
          if (videoRef.current && videoRef.current.readyState >= 2) {
            videoRef.current.play().catch(() => {})
            videoRef.current?.removeEventListener('canplay', tryPlayWhenReady)
          }
        }
        videoRef.current.addEventListener('canplay', tryPlayWhenReady, { once: true })
      }
    },
    [muted, linkHref]
  )

  // 全局用户交互监听（用户点击页面任意位置后，视频可以自动播放）
  useEffect(() => {
    if (type !== 'video' || stopAutoplay) return

    const handleGlobalInteraction = () => {
      hasUserInteractedRef.current = true
      // 立即尝试播放
      if (videoRef.current) {
        videoRef.current.muted = muted ?? true
        videoRef.current.play().catch(() => {
          // 播放失败，等待视频加载完成
          const tryPlayWhenReady = () => {
            if (videoRef.current && videoRef.current.readyState >= 2) {
              videoRef.current?.play().catch(() => {})
              videoRef.current?.removeEventListener('canplay', tryPlayWhenReady)
            }
          }
          videoRef.current?.addEventListener('canplay', tryPlayWhenReady, { once: true })
        })
      }
    }

    // 监听多个事件，确保能捕获到用户交互
    document.addEventListener('touchstart', handleGlobalInteraction, { passive: true })
    document.addEventListener('touchend', handleGlobalInteraction, { passive: true })
    document.addEventListener('click', handleGlobalInteraction, { passive: true })
    document.addEventListener('mousedown', handleGlobalInteraction, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleGlobalInteraction)
      document.removeEventListener('touchend', handleGlobalInteraction)
      document.removeEventListener('click', handleGlobalInteraction)
      document.removeEventListener('mousedown', handleGlobalInteraction)
    }
  }, [type, stopAutoplay, muted])

  // 视频播放逻辑
  useEffect(() => {
    if (type !== 'video' || stopAutoplay || !videoRef.current) return

    const video = videoRef.current
    const isWeChat = isWeChatBrowser()
    video.muted = muted ?? true

    // 视频加载完成后播放
    const handleCanPlay = () => {
      if (hasUserInteractedRef.current || !isWeChat) {
        playVideo()
      }
    }

    const handleLoadedData = () => {
      if (hasUserInteractedRef.current) {
        setTimeout(() => playVideo(), isWeChat ? 200 : 0)
      }
    }

    const handleLoadedMetadata = () => {
      if (hasUserInteractedRef.current) {
        setTimeout(() => playVideo(), isWeChat ? 100 : 0)
      }
    }

    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)

    // 微信浏览器特殊处理
    if (isWeChat) {
      const handleWeixinReady = () => {
        if (hasUserInteractedRef.current) {
          setTimeout(() => playVideo(), 300)
        }
      }
      if (typeof window !== 'undefined' && (window as any).WeixinJSBridge) {
        handleWeixinReady()
      } else {
        document.addEventListener('WeixinJSBridgeReady', handleWeixinReady, { once: true })
      }
    } else {
      // 非微信浏览器尝试自动播放
      playVideo()
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [type, stopAutoplay, muted, src, playVideo])

  useEffect(() => {
    if (videoCommand === 'PLAY') {
      videoRef.current?.play()
    } else if (videoCommand === 'PAUSE') {
      videoRef.current?.pause()
    }
  }, [videoCommand])

  return (
    <div className={clsx('relative w-full', autoHeight ? 'h-auto' : 'h-full', className)}>
      {type === 'image' &&
        !!src &&
        src !== 'undefined' &&
        (autoHeight ? (
          // fill 定位的图片不能撑开父容器，自适应模式交由浏览器按固有比例布局。
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={src}
            alt={alt || ''}
            loading={priority ? undefined : 'lazy'}
            className={clsx('block w-full h-auto', imageClassName)}
          />
        ) : (
          <PdImage
            src={src}
            h5Src={h5Src}
            useLoader={useLoader}
            object={object}
            objectPosition={'center'}
            alt={alt}
            enableMask={false}
            width={'100%'}
            priority={priority}
            enableHoverScale={enableHoverScale}
            className={imageClassName}
          />
        ))}

      {type === 'video' && (
        <video
          ref={videoRef}
          className={clsx(
            autoHeight ? 'block w-full h-auto object-cover object-center' : 'w-full h-full object-cover object-center',
            showShadow && 'shadow-lg'
          )}
          autoPlay={!stopAutoplay}
          loop={loop}
          muted={muted ?? true}
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x-webkit-airplay="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          x5-video-orientation="portraint"
          onClick={handleUserInteraction}
          onTouchStart={handleUserInteraction}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}

      {mask && (
        <div className="absolute top-0 right-0 bottom-0 left-0 w-full h-full overflow-hidden bg-fixed bg-black opacity-40 z-1"></div>
      )}
      <div className="absolute top-0 right-0 bottom-0 left-0 w-full h-full overflow-hidden">
        <a
          className={[
            'inline-block w-full h-full',
            centerChildren ? 'flex flex-col items-center' : '',
            !linkHref && 'cursor-default',
          ].join(' ')}
          href={linkHref}
        >
          {children}
        </a>
      </div>
    </div>
  )
}

export default PdMedia
